import React, {FC} from 'react';
import {OfferCard} from '@components/OfferCard';
import {PropertyPrice} from '@components/PropertyPrice';
import {FeaturesList} from '@components/FeaturesList';
import {Currency, Disposition, EstateType, OfferType} from '.cache/__types__';
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl';
import {translations} from '@constants/Disposition';
import Link from 'next/link';
import {getLocalizedRoute, routes} from '@utils/routes';
import NextImage from 'next/image';
import NoImagePlaceholder from 'images/default_foto_small.jpg';
import {useLocale} from '@utils/locale';
import {Divider} from "@components/Divider";

export type AdvertOfferCardProps = {
    imageUrl?: string;
    imageAlt?: string;
    surface: number;
    disposition?: Disposition;
    estateType: EstateType;
    offerType: OfferType;
    address: string;
    price: number;
    charges?: number;
    currency: Currency;
    uri: string;
};

export const AdvertOfferCard: FC<AdvertOfferCardProps> = ({
    imageUrl,
    imageAlt,
    surface,
    disposition,
    estateType,
    offerType,
    address,
    price,
    charges,
    currency,
    uri,
}) => {
    const locale = useLocale();
    const intl = useIntl();

    const alt =
        imageAlt ??
        intl.formatMessage(
            {
                defaultMessage:
                    '{offerType, select, PRODEJ {Prodej} PRONAJEM {Pronájem} other {Spolubydlení v rámci}} {estateType, select, BYT {bytu} DUM {domu} POZEMEK {pozemku} GARAZ {garáže} REKREACNI_OBJEKT {rekreačního objektu} NEBYTOVY_PROSTOR {nebytového prostoru} other {kanceláře}}',
            },
            {
                estateType,
                offerType,
            },
        );

    return (
        <OfferCard>
            <OfferCard.Image>
                <NextImage
                    src={!!imageUrl && imageUrl !== '' ? imageUrl : NoImagePlaceholder}
                    alt={alt}
                    title={alt}
                    placeholder={!!imageUrl ? 'empty' : 'blur'}
                    layout="fill"
                    objectFit={'cover'}
                />
            </OfferCard.Image>

            <OfferCard.Content>
                <Link
                    href={getLocalizedRoute(routes.detail, locale, {
                        slug: uri,
                    })}
                >
                    <a>
                        <OfferCard.Label>
                            <FormattedMessage
                                defaultMessage={
                                    '{offerType, select, PRODEJ {Prodej} other {Pronájem}} {estateType, select, BYT {bytu} DUM {domu} POZEMEK {pozemku} GARAZ {garáže} REKREACNI_OBJEKT {rekreačního objektu} NEBYTOVY_PROSTOR {nebytového prostoru} other {kanceláře}}'
                                }
                                values={{
                                    estateType,
                                    offerType,
                                }}
                            />
                        </OfferCard.Label>
                        <OfferCard.Address>
                            {address}
                        </OfferCard.Address>
                    </a>
                </Link>

                <Divider color="grey-light" className="my-3" />

                <OfferCard.Features>
                    <FeaturesList>
                        {disposition && <FeaturesList.Item iconName="FloorPlan">{translations[disposition]}</FeaturesList.Item>}
                        <FeaturesList.Item iconName="FloorArea">{surface}&nbsp;m²</FeaturesList.Item>
                    </FeaturesList>
                </OfferCard.Features>

                <Divider color="grey-light" className="my-3" />

                <OfferCard.Price>
                    <FormattedNumber value={price} style={'currency'} currency={currency} maximumFractionDigits={0}>
                        {(formattedPrice: string) => {
                            return charges ? (
                                <FormattedNumber value={charges} style={'currency'} currency={currency} maximumFractionDigits={0}>
                                    {(formattedCharges: string) => (
                                        <PropertyPrice amount={formattedPrice} additional={'+ ' + formattedCharges} />
                                    )}
                                </FormattedNumber>
                            ) : (
                                <PropertyPrice amount={formattedPrice} />
                            );
                        }}
                    </FormattedNumber>
                </OfferCard.Price>
            </OfferCard.Content>
        </OfferCard>
    );
};
